import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { consumePendingSignOut } from "@/hooks/use-centralized-auth";
import logo from "@/assets/angelhost-mark.png";

const Auth = () => {
  const { user, loading, signIn, isConfigured } = useAuth();

  // Consume a pending sign-out (set by signOut() before redirecting here).
  // Doing the actual token / storage / GoTrue cleanup on /auth — an
  // unauthenticated tree with no React Query / OwnerFilterContext consumers —
  // guarantees no provider cascade or main-thread freeze on logout.
  const signOutHandledRef = useRef(false);
  useEffect(() => {
    if (signOutHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    let hasFlag = params.get("signout") === "1";
    if (!hasFlag) {
      try { hasFlag = sessionStorage.getItem("auth:pending-signout") === "1"; }
      catch { /* ignore */ }
    }
    if (!hasFlag) return;
    signOutHandledRef.current = true;
    void consumePendingSignOut();
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("signout");
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
  }, []);


  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reasonShownRef = useRef(false);
  useEffect(() => {
    if (reasonShownRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason");
    if (!reason) return;
    reasonShownRef.current = true;
    if (reason === "session_expired") {
      toast({
        title: "Session expired",
        description:
          "Your session expired or your access to the Core was revoked. Please sign in again.",
        variant: "destructive",
      });
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("reason");
    window.history.replaceState({}, "", url.toString());
  }, []);

  if (user && !loading) {
    return <Navigate to="/" replace />;
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfigured) {
      toast({ title: "Not configured", description: "Supabase environment variables are missing.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await signIn(signInEmail, signInPassword);
    setSubmitting(false);
    if (error) {
      toast({ title: "Sign in failed", description: error.message, variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img
            src={logo}
            alt="Client Hub"
            className="h-16 w-auto object-contain mb-6"
            loading="eager"
            decoding="sync"
          />
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Log in to your account
          </h1>
        </div>

        <form onSubmit={handleSignIn} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="signin-email" className="text-sm font-semibold text-foreground">
              Email
            </Label>
            <Input
              id="signin-email"
              type="email"
              placeholder="myemail@email.com"
              value={signInEmail}
              onChange={(e) => setSignInEmail(e.target.value)}
              required
              className="h-12 rounded-lg border-border bg-card text-base placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="signin-password" className="text-sm font-semibold text-foreground">
              Password
            </Label>
            <div className="relative">
              <Input
                id="signin-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={signInPassword}
                onChange={(e) => setSignInPassword(e.target.value)}
                required
                className="h-12 rounded-lg border-border bg-card pr-12 text-base placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 rounded-lg text-base font-medium"
            disabled={submitting}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </main>
  );
};

export default Auth;
